import { useState } from 'react';

import { useQuery } from '@zg-rentals/react-query5';
import { RentalManagerAddressAutocompleteWebApiHandler_autocomplete } from '@zg-rentals/ts-rental-manager-api-client';

import AutoCompleteAddresses from '../model/AutoCompleteAddresses';

import { useDebouncedValue } from './useDebouncedValue';

const MIN_LENGTH_REQUIRED_BY_API = 7;
const MAX_LENGTH_REQUIRED_BY_API = 100;

type UseAddressAutocompleteOptions = {
  /** When set, wait this long after the last keystroke before querying. Off by default. */
  debounceMs?: number;
};

const useAddressAutocomplete = ({ debounceMs = 0 }: UseAddressAutocompleteOptions = {}) => {
  const [partialAddress, setPartialAddress] = useState('');

  // Each distinct value is its own query key, so an undebounced caller fires one request per keystroke.
  const queriedAddress = useDebouncedValue(partialAddress, debounceMs);

  const queryFn = () => RentalManagerAddressAutocompleteWebApiHandler_autocomplete({ partialAddress: queriedAddress });

  const queryHook = useQuery({
    queryKey: ['getAddressAutocomplete', queriedAddress],
    queryFn: async () => {
      const data = await queryFn();
      return AutoCompleteAddresses(data);
    },
    staleTime: 10 * 1000,
    enabled: Boolean(
      queriedAddress.trim().length > MIN_LENGTH_REQUIRED_BY_API &&
      queriedAddress.trim().length <= MAX_LENGTH_REQUIRED_BY_API,
    ),
    meta: {
      tags: ['getAddressAutocomplete'],
      context: {
        api: 'rental-manager',
        details: 'RentalManagerAddressAutocompleteWebApiHandler_autocomplete',
      },
    },
  });

  return [setPartialAddress, queryHook] as const;
};

export { useAddressAutocomplete };
